import { Avatar } from "@moe/oss/ui/avatar";
import { Persona } from "@moe/priv/model/persona";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChatPreview } from "@web/components/ChatPreview";
import { LoadingSpinner } from "@web/components/LoadingSpinner";
import { useInfiniteScroll } from "@web/hooks/use-infinite-scroll";
import { PersonaSelectorModal } from "@web/route-services/chat/PersonaSelector";
import { useChatPreviewQuery } from "@web/route-services/chat/use-chat-preview-query";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useState } from "react";

export interface Props {
  chatID?: number;
}

export function Sidepane({ chatID }: Props) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { createModal, access } = useAppContext();
  const queryClient = useQueryClient();

  const { data: chatPreviewPages, fetchNextPage, isPending, isError, isSuccess } = useChatPreviewQuery();
  const chatPreviews = chatPreviewPages?.pages.flatMap((page) => page) || [];

  const { data: persona } = useQuery({
    queryKey: ["chatPersona", chatID],
    queryFn: async () => {
      return await access.persona.getByChatID(chatID!);
    },
    enabled: !!chatID
  });

  const scrolledToBottomHandler = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const { topSentinelRef, bottomSentinelRef } = useInfiniteScroll({ items: chatPreviews, scrolledToBottomHandler });

  const personaSelectorHandler = () => {
    if (!chatID) throw new Error("Chat ID is required to select a persona");

    const selectHandler = async (persona: Persona) => {
      await access.chat.update({ id: chatID, personaID: persona.id });
      queryClient.invalidateQueries({ queryKey: ["chatPersona", chatID] });
    };
    createModal({ content: <PersonaSelectorModal onSelect={selectHandler} /> });
  };

  return (
    <div className="flex h-full flex-row items-center">
      {/* Main Part of Sidebar */}
      <AnimatePresence initial={false}>
        {sidebarOpen && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{
              width: 320,
              opacity: 1,
              transition: {
                type: "spring",
                stiffness: 300,
                damping: 30
              }
            }}
            exit={{
              width: 0,
              opacity: 0,
              transition: {
                type: "spring",
                stiffness: 300,
                damping: 30
              }
            }}
            className="bg-chat-sidebar-background flex h-full flex-col overflow-hidden"
          >
            {/* Recent Chats */}
            <div className="scroll-chat-sidebar flex grow flex-col overflow-x-hidden overflow-y-scroll px-2 py-4">
              <div ref={topSentinelRef} className="w-full" />
              {isPending && <LoadingSpinner className="m-auto" />}

              {isError && (
                <p className="text-err m-auto select-none text-center text-xl leading-9">Failed to load chats.</p>
              )}

              {isSuccess &&
                chatPreviews?.map((preview, idx, arr) => (
                  <ChatPreview
                    key={preview.chatID}
                    chatID={chatID}
                    preview={preview}
                    className={`${idx < arr.length - 1 ? "mb-4" : ""}`}
                  />
                ))}
              <div ref={bottomSentinelRef} className="w-full" />
            </div>

            {/*Footer Section */}
            {chatID && (
              <motion.div className="bg-chat-sidebar-footer relative mt-1 flex h-20 w-full shrink-0 flex-row p-4">
                {/* Fade */}
                <div
                  className={`from-chat-sidebar-footer pointer-events-none absolute -top-5 left-0 z-10 h-5 w-full bg-gradient-to-t to-transparent transition duration-300 ease-out`}
                />

                <AnimatePresence mode="popLayout">
                  {persona && (
                    <motion.button
                      className="flex flex-row items-center"
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                      onClick={personaSelectorHandler}
                    >
                      <Avatar src={persona.avatar} alt="Avatar" className="size-10" />

                      <div className="flex h-full flex-col justify-center pl-3">
                        <p className="text-tx-primary font-bold ">{persona.name}</p>
                      </div>
                    </motion.button>
                  )}
                </AnimatePresence>
                <div className="grow"></div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sidebar Toggle Button */}
      <button
        className={`group ${sidebarOpen ? "ml-1.5" : "ml-1"} flex h-10 w-3.5 items-center justify-center rounded-full`}
        onClick={() => {
          setSidebarOpen(!sidebarOpen);
        }}
      >
        <div className="bg-action-tertiary h-10 w-1.5 rounded-full transition duration-300 ease-out group-hover:brightness-125"></div>
      </button>
    </div>
  );
}
